// ONE builder for a bundle's part rig — the rig, its frame, and how a pose reaches a branch.
// Notes: ~/comments/aos-threejs-poc/partDriveBuilder_buildPartDrive_one_builder.md

import type * as THREE from "three";
import { loadPartDriver, formatPartDriverFit } from "../inference/partDriver.js";
import { applyPartRigPose, formatPartRigPose } from "../inference/partBodyPose.js";
import { rigToWorldRotation, rigToWorldScale } from "../inference/rigRenderFrame.js";
import type { OGSBranch } from "../graph/nodes/OGSBranch.ts";
import type { OgsLogger } from "./ogsLog.ts";
import type { PartPoser } from "./PartRigPipeline.ts";
import { installLiveBodySkin } from "../branch/liveBodySkin.js";
import { checkRenderContract } from "../render/renderContract.js";

/** What the body blueprint and a clip preview consume (partDriveRegistry). */
export interface PartDriveHandle extends PartPoser {
  /** True for a branch whose OWN deform produces its verts — the caller's own test. */
  isLive(branch: OGSBranch): boolean;
  /** The body skinners this drive owns (`installLiveBodySkin`), so a character
   *  switch can free them — they hold a bind group over the lifter's vertex buffer. */
  bodySkinners: Map<string, unknown>;
  /** The loaded skeleton — clips bind to it. */
  rig: { clip?: { duration: number } | null; parts: unknown[]; setTime(t: number): void; refreshBones?(): void };
  /** Null until something binds a clip: the rig arrives with a skeleton only. */
  duration: number | null;
  /** The rotation this rig's own space is RENDERED through (rigRenderFrame.js). */
  rigToWorld: unknown;
  /** Metres per rig unit — rig.glb is centimetres and nothing above `pelvis` says so. */
  rigToWorldScale: unknown;
}

export interface BuildPartDriveInput {
  rigUrl: string;
  branchMeshes: Record<string, unknown>;
  branches: OGSBranch[];
  worldRotation: THREE.Quaternion | null;
  worldScale: number;
  /** True for a branch whose OWN deform produces its verts, so the body must SKIN
   *  it rather than carry it rigidly on one anchor.
   *
   *  REQUIRED, and it used to default to `() => false`. That default is the
   *  "no silent fallbacks" rule's exact shape: "nothing here is live" is a
   *  perfectly ordinary answer for some bundles, so a caller that simply forgot
   *  to pass one was indistinguishable from a correct one — and the next render
   *  path forgot. Every branch was filed as frozen, the displacement head took a
   *  rigid delta on its anchor, and the head lifted off the collar the moment the
   *  character walked. `renderContract.js` could not catch it either, because the
   *  absence it reports is "a LIVE branch is unskinned" and this default said
   *  there were none. */
  isLive: (branch: OGSBranch) => boolean;
  isCancelled: () => boolean;
  /** The device a live branch is skinned on. Omitted (or null) keeps every live
   *  branch on the rigid anchor — which is a visible defect on a walk, so
   *  `renderContract.js` reports it rather than leaving it to be noticed. */
  device?: GPUDevice | null;
  log: OgsLogger;
}

/** Load a bundle's `rig.glb` and return the poser. Null when cancelled mid-load.
 *
 *  THE BODY SKINNER IS INSTALLED HERE, not by the caller. It used to be wired in
 *  `OGSModel.jsx`, so the other render path never did it and its live branches
 *  silently took the rigid-anchor fallback — a head that separates at the collar
 *  the moment the character walks. Both paths call this function, so installing
 *  it here is what makes a live branch skinned BY CONSTRUCTION. */
export async function buildPartDrive(input: BuildPartDriveInput): Promise<PartDriveHandle | null> {
  const { branches, log } = input;
  // Loud, at the one place both paths pass through. A caller with genuinely no
  // live branches says so explicitly — `() => false` is a fine ANSWER and was a
  // catastrophic DEFAULT.
  if (typeof input.isLive !== "function") {
    throw new TypeError("buildPartDrive: `isLive` is required — pass the pipeline's own test "
      + "(a branch whose own deform produces its verts). Defaulting it to 'nothing is live' "
      + "files the displacement head as frozen and lifts it off the collar on a walk.");
  }
  const isLive = input.isLive;
  const driver = await loadPartDriver({ rigUrl: input.rigUrl, branchMeshes: input.branchMeshes });
  if (input.isCancelled()) { driver.rig.dispose(); return null; }

  // The rotation the rig's own space is RENDERED through: the solved part fit (rig frame →
  // trained frame) then the bundle's world_rotation. Composed here because this is the one
  // scope that knows both halves. Locals, not fields bolted onto the loader's object.
  const rigToWorld = rigToWorldRotation(driver.verts, input.worldRotation);
  const rigToWorldScale_ = rigToWorldScale(input.worldScale);
  // The frame contract, per part: maxDisagreement reads ~0 even when every vertex is in
  // the wrong frame, so the fit is logged rather than merely checked.
  for (const branch of driver.fit) log.info(formatPartDriverFit(branch));

  // Live branches are skinned by the body rig when the bundle and the device
  // allow it; each refusal names its reason and keeps the anchor.
  const bodySkinners = await installLiveBodySkin({
    driver, branches, isLive, device: input.device ?? null, log,
  });
  // WHAT THIS DRIVE OWES THE FRAME, checked once, out loud. A live branch that
  // ends up on the rigid anchor while a device and a `rig.glb` part exist is a
  // defect that shows as a head separating at the collar — reported here rather
  // than found on screen. `not-applicable` (no device, no live branch) is
  // silent, because it is not a failure.
  checkRenderContract({ branches, device: input.device ?? null, isLive }, { log, source: "part-drive" });

  // Own buffer per rigid rider — neutralVertices must never be mutated, and the
  // no-per-frame-alloc rule means it cannot be made inside pose().
  // `verts.branches` is a plain name→part map from untyped JS; read it as one.
  const partFor = driver.verts.branches as Record<string, unknown>;
  const rigidVerts = new Map(branches
    .filter((branch) => !partFor[branch.name] && !isLive(branch))
    .map((branch) => [branch.name, new Float32Array(branch.neutralVertices.length)]));

  let reported = false;
  /** Applies the CURRENT pose and returns the branches whose ANCHOR moved. */
  const applyPose = (at: number | null): OGSBranch[] => {
    const report = applyPartRigPose({
      branches,
      verts: driver.verts,
      headDelta: driver.rig.headDelta(driver.verts.fitForPart?.(driver.rig.headRestPart)),
      rigidVerts,
      isLive,
      at,
    });
    if (!reported) {
      reported = true;
      log.info(`part drive posing — ${formatPartRigPose(report)}`);
    }
    // The live branches are the ones the engine must re-pose: their verts come from their
    // own deform, so a moved anchor only reaches the lifter through a pose.
    return branches.filter((branch) => isLive(branch));
  };

  const duration = driver.rig.clip?.duration ?? null;
  // Name the rigid riders: a branch that quietly holds neutral for a whole clip reads as a
  // broken drive, and the cause is one missing part in rig.glb.
  const riders = [...rigidVerts.keys()];
  const rigid = !riders.length ? "none"
    : driver.rig.headDelta() ? `${riders.join(", ")} on the head bone`
      : `${riders.join(", ")} STATIC — the rig GLB has no head bone`;
  log.info(`part drive ready (${driver.rig.parts.length} parts, `
    + `${duration ? `${duration.toFixed(2)}s clip` : "no clip yet"}, rigid: ${rigid})`);

  return {
    /** The skinners this drive owns, so a character switch can free them — they
     *  hold a bind group over the lifter's vertex buffer, which the next
     *  character's lifter does not own. */
    bodySkinners,
    pose(t: number): OGSBranch[] {
      driver.rig.setTime(t);
      return applyPose(t);
    },
    isLive,
    poseFace() {
      // The joints changed and the body did not, so only the head is re-skinned: a
      // full-body pose per face frame is what slowed the walk.
      driver.rig.refreshBones();
      const head = branches.find((branch) => branch.name === "head");
      const posed = driver.verts.deform("head");
      if (!posed || !head) return;
      head.verts = posed;
      head.lifter?.computeJacobians(posed);
      // The head alone moved to the CURRENT bones while every other branch keeps
      // the verts from the last full pose. That is correct only while the body is
      // still, which `driveFace`'s `slot.driveOwned` guard is what guarantees —
      // so the stamp records it and a divergence here is visible rather than
      // inferred.
      head.lastPosedAt = driver.rig.currentTime ?? head.lastPosedAt ?? null;
    },
    rig: driver.rig,
    duration,
    rigToWorld,
    rigToWorldScale: rigToWorldScale_,
  };
}
